// src/app/services/analytics/relative-time.ts
//
// "2 hours ago", "yesterday", "3 days ago".
//
// A timestamp answers "is this fresh?", and a person answers that from elapsed
// time, not from a date they have to subtract in their head. The exact instant
// is never thrown away: every caller puts the full local date and time in a
// `title`, so the precise answer is one hover away and the readable one is on
// screen.
//
// Pure, and the clock is an argument rather than a global, which is the only way
// a relative time can be tested at all. No dependency: the app carries date-fns
// but none of this needs it.

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Whole calendar days between two instants, in LOCAL time. Calendar days,
 *  because "3 days ago" is a count of midnights crossed and not a division by
 *  86,400,000: an elapsed-hours count calls a read at 23:00 last night and one
 *  at 01:00 this morning the same age, and they are not. */
function calendarDaysBetween(then: Date, now: Date): number {
  const a = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((b - a) / DAY);
}

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"} ago`;

/**
 * A timestamp as elapsed time, or null when there is nothing to say.
 *
 * Null rather than a dash or an empty string: an absence is never a zero, and
 * the caller says what the absence means in its own words ("No read yet")
 * rather than printing a gap.
 *
 * A timestamp in the future is clock skew between this browser and the server,
 * not a prediction, so it reads as "just now" rather than counting down.
 */
export function relativeTime(
  iso: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!iso) return null;
  const then = new Date(iso);
  const at = then.getTime();
  if (!Number.isFinite(at)) return null;

  const elapsed = now.getTime() - at;
  if (elapsed < MINUTE) return "just now";
  if (elapsed < HOUR) return plural(Math.floor(elapsed / MINUTE), "minute");
  if (elapsed < DAY) return plural(Math.floor(elapsed / HOUR), "hour");

  const days = calendarDaysBetween(then, now);
  if (days <= 1) return "yesterday";
  if (days < 7) return plural(days, "day");
  if (days < 30) return plural(Math.floor(days / 7), "week");
  if (days < 365) return plural(Math.floor(days / 30), "month");
  return plural(Math.floor(days / 365), "year");
}

/** The exact instant, for the `title` that sits behind the readable one. Null on
 *  the same terms, so a caller cannot end up with one and not the other. */
export function exactTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isFinite(at.getTime()) ? at.toLocaleString() : null;
}
